import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { lazyImport } from "./utils/lazyImport";

// Lazy load pages for better code splitting
const Index = lazyImport(() => import("./pages/Index"));
const Login = lazyImport(() => import("./pages/Login"));
const SignUp = lazyImport(() => import("./pages/SignUp"));
const ForgotPassword = lazyImport(() => import("./pages/ForgotPassword"));
const Dashboard = lazyImport(() => import("./pages/Dashboard"));
const TeamMembers = lazyImport(() => import("./pages/TeamMembers"));
const UserProfile = lazyImport(() => import("./pages/UserProfile"));
const OAuthCallback = lazyImport(() => import("./pages/OAuthCallback"));
const Docs = lazyImport(() => import("./pages/Docs"));
const Contact = lazyImport(() => import("./pages/Contact"));
const Onboarding = lazyImport(() => import("./pages/Onboarding"));
const NotFound = lazyImport(() => import("./pages/NotFound"));

// Lazy load dashboard components
const DashboardLayout = lazyImport(() => import("./components/dashboard/DashboardLayout").then(m => ({ default: m.DashboardLayout })));
const Analytics = lazyImport(() => import("./components/dashboard/analytics/AnalyticsDashboard").then(m => ({ default: m.AnalyticsDashboard })));
const ConversationsList = lazyImport(() => import("./components/dashboard/conversations/ConversationsList").then(m => ({ default: m.ConversationsList })));
const ConversationDetail = lazyImport(() => import("./components/dashboard/conversations/ConversationDetail").then(m => ({ default: m.ConversationDetail })));
const KnowledgeBase = lazyImport(() => import("./components/dashboard/knowledge/KnowledgeBase").then(m => ({ default: m.KnowledgeBase })));
const LeadsList = lazyImport(() => import("./components/dashboard/leads/LeadsList").then(m => ({ default: m.LeadsList })));
const AppointmentsList = lazyImport(() => import("./components/dashboard/appointments/AppointmentsList").then(m => ({ default: m.AppointmentsList })));
const Settings = lazyImport(() => import("./components/dashboard/settings/TabsSettingsView").then(m => ({ default: m.TabsSettingsView })));
const IntegrationsView = lazyImport(() => import("./components/dashboard/integrations/IntegrationsView").then(m => ({ default: m.IntegrationsView })));
const DeveloperSettings = lazyImport(() => import("./components/dashboard/developer/DeveloperSettings").then(m => ({ default: m.DeveloperSettings })));
const ModerationDashboard = lazyImport(() => import("./components/dashboard/moderation/ModerationDashboard").then(m => ({ default: m.ModerationDashboard })));
const PerformanceMetrics = lazyImport(() => import("./components/dashboard/performance/PerformanceMetrics").then(m => ({ default: m.PerformanceMetrics })));
const BillingView = lazyImport(() => import("./components/dashboard/billing/BillingView").then(m => ({ default: m.BillingView })));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppProvider>
          <AuthProvider>
            <ProjectProvider>
              <Suspense fallback={<PageLoader />}>

                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/onboarding" element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  } />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/integrations/oauth/callback" element={<OAuthCallback />} />
                  <Route path="/oauth-callback" element={<OAuthCallback />} />
                  <Route path="/docs" element={<Docs />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<Dashboard />} />
                    <Route path="analytics" element={<Analytics />} />
                    <Route path="conversations" element={<ConversationsList />} />
                    <Route path="conversations/:id" element={<ConversationDetail />} />
                    <Route path="leads" element={<LeadsList />} />
                    <Route path="appointments" element={<AppointmentsList />} />
                    <Route path="knowledge" element={<KnowledgeBase />} />
                    <Route path="team" element={<TeamMembers />} />
                    <Route path="profile" element={<UserProfile />} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="integrations" element={<IntegrationsView />} />
                    <Route path="developer" element={<DeveloperSettings />} />
                    <Route path="moderation" element={<ModerationDashboard />} />
                    <Route path="performance" element={<PerformanceMetrics />} />
                    <Route path="billing" element={<BillingView />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ProjectProvider>
          </AuthProvider>
        </AppProvider>
      </Router>
    </ErrorBoundary >
  );
}

export default App;
